import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import i18n from "@/i18n";
import BackButton from "@/components/back-button";
import { Divider } from "@/components/divider";
import { LinkArrowLeft } from "@/components/icons";
import ReviewCard from "@/components/review-card";
import { reviewCategories } from "@/routes/app/profile/-categories";
import { localeFor } from "@/utils/formatters";

import { makeProfileQuery, makeReviewQuery } from "../-queries";

export const Route = createFileRoute(
  "/app/profile/employer/$employerId/ratings/",
)({
  component: RouteComponent,
  staticData: {
    layout: {
      bg: "fog",
      boxed: true,
    },
  },
});

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-fog w-[110px] h-[80px] flex flex-col items-center justify-center px-4">
      {children}
    </div>
  );
}

function RouteComponent() {
  const { t } = useTranslation("profile");
  const { employerId } = Route.useParams();
  const profileQuery = makeProfileQuery(employerId);
  const reviewsQuery = makeReviewQuery(employerId);

  const { data } = useSuspenseQuery(profileQuery);
  const { data: reviewsData } = useSuspenseQuery(reviewsQuery);
  const { business } = data;
  const reviews = reviewsData.reviews;

  function overallRating(reviewsParam: Array<any>) {
    if (reviewsParam.length === 0) return "—";
    const total = reviewsParam.reduce((acc: number, review: any) => {
      if (!review.rating) return acc;
      Object.keys(review.rating).forEach((r) => {
        acc += review.rating[r] || 0;
      });
      return acc / Object.keys(review.rating).length;
    }, 0);
    return total.toFixed(1);
  }

  const average_rating = overallRating(reviews.filter((r: any) => r.rating));

  const categoryAverages = reviewCategories.reduce(
    (acc, cat) => {
      const categoryReviews = reviews.filter(
        (r: any) => r.rating && r.rating[cat.value] !== undefined,
      );
      if (categoryReviews.length === 0) {
        acc[cat.value] = "—";
      } else {
        const total = categoryReviews.reduce(
          (sum: number, r: any) => sum + (r.rating[cat.value] || 0),
          0,
        );
        acc[cat.value] = (total / categoryReviews.length).toFixed(1);
      }
      return acc;
    },
    {} as Record<string, string>,
  );

  return (
    <div className="w-full">
      <div className="mb-6">
        <BackButton />
      </div>
      <div className="flex flex-row gap-6 w-full">
        {/* Left Card: Rating Summary */}
        <div className="min-w-[400px] w-[400px]">
          <div className="bg-white rounded-2xl p-6 flex flex-col gap-4">
            <div className="flex flex-col items-center gap-2">
              <div className="w-16 h-16 rounded-full bg-fog flex items-center justify-center text-3xl font-bold text-ember border-2 border-ember mb-2">
                {business.total_reviews}
                <span className="text-lg align-super">★</span>
              </div>
              <div className="text-lg font-semibold">
                {business.name ?? t("ratings.employerFallback")}
              </div>
              <div className="text-xs text-muted-foreground">
                {t("ratings.activeSince", {
                  date: business.createdAt
                    ? new Date(business.createdAt).toLocaleString(
                        localeFor(i18n.language || "en"),
                        {
                          month: "short",
                          year: "numeric",
                        },
                      )
                    : "—",
                })}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-1 w-full mt-2">
              <Chip>
                <span
                  className="body-xs text-center"
                  style={{ wordSpacing: 500 }}
                >
                  {t("ratings.overallRating")}
                </span>
                <span className="text-[20px] font-bold bg-gradient-gold bg-clip-text ">
                  {average_rating}★
                </span>
              </Chip>
              {[
                ...reviewCategories.map((c) => ({
                  label: c.label,
                  value: categoryAverages[c.value],
                })),
              ].map((item) => (
                <Chip key={item.label}>
                  <span className="body-xs text-center">{item.label}</span>
                  <div className="font-bold text-ember">{item.value}</div>
                </Chip>
              ))}
            </div>
            <div className="text-sm text-center mt-2 text-muted-foreground">
              {business.description ?? t("ratings.noDescription")}
            </div>
            <Divider />
            <div className="w-full flex flex-col mt-12 border-t-[1px] border-[#545A6340] border-b-[1px]">
              <Link
                to="/app/profile/employer/$employerId/review"
                params={{ employerId: String(business.id ?? 0) }}
                className="no-underline!"
              >
                <div className="flex flex-row items-center w-full justify-between h-10 body-small  font-semibold">
                  <div className="flex flex-row items-center justify-center gap-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M13.7513 0.625V3.125H16.2513C16.5951 3.125 16.8763 3.40625 16.8763 3.75C16.8763 4.09375 16.5951 4.375 16.2513 4.375H13.7513V6.875C13.7513 7.21875 13.4701 7.5 13.1263 7.5C12.7826 7.5 12.5013 7.21875 12.5013 6.875V4.375H10.0013C9.65758 4.375 9.37633 4.09375 9.37633 3.75C9.37633 3.40625 9.65758 3.125 10.0013 3.125H12.5013V0.625C12.5013 0.28125 12.7826 0 13.1263 0C13.4701 0 13.7513 0.28125 13.7513 0.625ZM5.97008 10.3711C5.78649 10.7422 5.43493 10.9961 5.02868 11.0547L1.61071 11.5547L4.08727 13.9727C4.38024 14.2578 4.51696 14.6719 4.44665 15.0781L3.86071 18.4883L6.9154 16.8789C7.27868 16.6875 7.71618 16.6875 8.07946 16.8789L11.1341 18.4883L10.5482 15.0781C10.4779 14.6719 10.6146 14.2617 10.9076 13.9727L13.3841 11.5547L9.96618 11.0586C9.55993 11 9.20837 10.7422 9.02477 10.375L7.49743 7.27344L5.97008 10.375V10.3711ZM6.65758 6.14844C7.00133 5.44922 7.99743 5.44922 8.34118 6.14844L10.1498 9.82031L14.1927 10.4102C14.9623 10.5234 15.267 11.4648 14.7123 12.0078L11.7865 14.8672L12.4779 18.9023C12.6107 19.668 11.806 20.2539 11.1185 19.8906L7.50133 17.9844L3.88024 19.8906C3.19274 20.2539 2.38805 19.668 2.52087 18.9023L3.21227 14.8672L0.282584 12.0078C-0.272103 11.4648 0.0364903 10.5195 0.802115 10.4102L4.84508 9.82031L6.65368 6.14844H6.65758ZM17.5013 6.25C17.8451 6.25 18.1263 6.53125 18.1263 6.875V8.125H19.3763C19.7201 8.125 20.0013 8.40625 20.0013 8.75C20.0013 9.09375 19.7201 9.375 19.3763 9.375H18.1263V10.625C18.1263 10.9688 17.8451 11.25 17.5013 11.25C17.1576 11.25 16.8763 10.9688 16.8763 10.625V9.375H15.6263C15.2826 9.375 15.0013 9.09375 15.0013 8.75C15.0013 8.40625 15.2826 8.125 15.6263 8.125H16.8763V6.875C16.8763 6.53125 17.1576 6.25 17.5013 6.25Z"
                        fill="black"
                      />
                    </svg>
                    {t("ratings.leaveReview")}
                  </div>
                  <LinkArrowLeft />
                </div>
                <Divider />
              </Link>
              <div className="flex flex-row items-center w-full justify-between h-10 body-small font-semibold">
                <div className="flex flex-row items-center justify-center gap-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M10 1.25C12.3206 1.25 14.5462 2.17187 16.1872 3.81282C17.8281 5.45376 18.75 7.67936 18.75 10C18.75 12.3206 17.8281 14.5462 16.1872 16.1872C14.5462 17.8281 12.3206 18.75 10 18.75C7.67936 18.75 5.45376 17.8281 3.81282 16.1872C2.17187 14.5462 1.25 12.3206 1.25 10C1.25 7.67936 2.17187 5.45376 3.81282 3.81282C5.45376 2.17187 7.67936 1.25 10 1.25ZM10 20C12.6522 20 15.1957 18.9464 17.0711 17.0711C18.9464 15.1957 20 12.6522 20 10C20 7.34784 18.9464 4.8043 17.0711 2.92893C15.1957 1.05357 12.6522 0 10 0C7.34784 0 4.8043 1.05357 2.92893 2.92893C1.05357 4.8043 0 7.34784 0 10C0 12.6522 1.05357 15.1957 2.92893 17.0711C4.8043 18.9464 7.34784 20 10 20ZM10 5C9.65625 5 9.375 5.28125 9.375 5.625V10.625C9.375 10.9688 9.65625 11.25 10 11.25C10.3438 11.25 10.625 10.9688 10.625 10.625V5.625C10.625 5.28125 10.3438 5 10 5ZM10.9375 13.75C10.9375 13.5014 10.8387 13.2629 10.6629 13.0871C10.4871 12.9113 10.2486 12.8125 10 12.8125C9.75136 12.8125 9.5129 12.9113 9.33709 13.0871C9.16127 13.2629 9.0625 13.5014 9.0625 13.75C9.0625 13.9986 9.16127 14.2371 9.33709 14.4129C9.5129 14.5887 9.75136 14.6875 10 14.6875C10.2486 14.6875 10.4871 14.5887 10.6629 14.4129C10.8387 14.2371 10.9375 13.9986 10.9375 13.75Z"
                      fill="black"
                    />
                  </svg>
                  {t("ratings.reportProblem")}
                </div>
                <LinkArrowLeft />
              </div>
            </div>
          </div>
        </div>
        {/* Right Card: Reviews */}
        <div className="bg-white rounded-2xl p-6 min-h-[500px] flex flex-col gap-6 w-full">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <div className="text-lg font-semibold mb-2 md:mb-0">
              {t("ratings.reviews")}
            </div>
            <div className="flex items-center gap-4 w-full md:w-auto">
              <div className="flex gap-2 text-sm font-semibold">
                <span className="text-ember border-b-2 border-ember pb-1 cursor-pointer">
                  {t("ratings.received")}
                </span>
                <span className="text-muted-foreground pb-1 cursor-pointer">
                  {t("ratings.sent")}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-6 mt-2">
            {reviews.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                {t("ratings.noReviews")}
              </div>
            ) : (
              <div className="space-y-6">
                {reviews.map((r: any) => (
                  <div key={r.id} className="">
                    <ReviewCard review={r} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
